// Connectors with NO account-level auth family — the workspace supplies credentials in-app.
//
// This file is an addition to the split the ticket sketches (`{google,meta,tiktok,social,
// ecommerce,mmp,database}`). Without it these eleven have to sit in `social.ts`, which would then
// hold App Store Connect, OpenAI Ads, RTB House and Hardal under a name that describes none of
// them. The ticket asks for "≥2 files, split per family/category"; eight files keeps every
// filename true and every file far under the 400-line cap.
//
// Apple Search Ads is the one non-`credential` member: it POSTs
// `/oauth/apple_search_ads/login/{token}` and then selects an account
// (`AppleSearchIntegration.vue:213`), so it is `oauthPost` — but it has no `AUTHENTICATIONS` entry,
// which is what puts it here rather than in `social.ts`.

import type { IntegrationProvider } from '../providers.types'

export const CREDENTIAL_PROVIDERS: readonly IntegrationProvider[] = [
  {
    key: 'rtb_account',
    id: 'rtb_account',
    nameKey: 'integrations.providers.rtbHouse',
    category: 'paid_advertising',
    connect: 'credential',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    labelFields: ['name', 'currency'],
    authLabelKey: 'integrations.authLabels.emailPassword',
    logo: 'rtb_house',
  },
  {
    key: 'app_store',
    id: 'app_store',
    nameKey: 'integrations.providers.appStoreConnect',
    category: 'analytics_and_tracking',
    connect: 'credential',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    labelFields: ['app_name'],
    authLabelKey: 'integrations.authLabels.apiCredentials',
    logo: 'apple',
  },
  {
    key: 'apple_search_ads_account',
    id: 'apple_search_ads_account',
    nameKey: 'integrations.providers.appleSearchAds',
    category: 'paid_advertising',
    connect: 'oauthPost',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    labelFields: ['app_name', 'adam_id'],
    authLabelKey: 'integrations.authLabels.apiCredentials',
    logo: 'apple_search_ads',
  },
  {
    key: 'yango_account',
    id: 'yango_account',
    nameKey: 'integrations.providers.yango',
    category: 'paid_advertising',
    connect: 'credential',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    labelFields: ['token'],
    labelEntities: { key: 'entities', fields: ['name', 'entity_id'] },
    allowsMultiple: true,
    logo: 'yango',
  },
  {
    key: 'reklamstore_account',
    id: 'reklamstore_account',
    nameKey: 'integrations.providers.reklamStore',
    category: 'paid_advertising',
    connect: 'credential',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    labelFields: ['network_id'],
    authLabelKey: 'integrations.authLabels.networkIdApiKey',
    logo: 'reklamstore',
  },
  {
    key: 'gelirortaklari_account',
    id: 'gelirortaklari_account',
    nameKey: 'integrations.providers.gelirOrtaklari',
    category: 'paid_advertising',
    connect: 'credential',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    labelFields: ['network_id'],
    authLabelKey: 'integrations.authLabels.networkIdApiKey',
    logo: 'gelir_ortaklari',
  },
  {
    key: 'openai_ads_account',
    id: 'openai_ads_account',
    nameKey: 'integrations.providers.openAiAds',
    category: 'paid_advertising',
    connect: 'credential',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    // v3 `fields: ['name']` — the label is the account name; the API key is a secret, never shown.
    labelFields: ['name'],
    logo: 'openai_ads',
  },
  {
    key: 'akakce_account',
    id: 'akakce_account',
    nameKey: 'integrations.providers.akakce',
    category: 'paid_advertising',
    connect: 'credential',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    labelFields: ['created_at', 'akakce_account_id'],
    authLabelKey: 'integrations.authLabels.apiKeySecret',
    logo: 'akakce',
  },
  // Ad revenue reporting, not an MMP: no `exclusiveGroup`, so it is offered beside whichever MMP the
  // workspace holds. The connect writes the constant name 'AppLovin MAX' (the Reporting API
  // publishes no account name), and connecting again replaces the workspace's one key — which is
  // what `allowsMultiple` offers (v3 `utils/integrations.js`, ORP-27450).
  {
    key: 'applovin_max_account',
    id: 'applovin_max_account',
    nameKey: 'integrations.providers.applovinMax',
    category: 'analytics_and_tracking',
    connect: 'credential',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    labelFields: ['name'],
    authLabelKey: 'integrations.authLabels.reportKey',
    allowsMultiple: true,
    logo: 'applovin_max',
  },
  // A workspace can hold several RevenueCat projects: a key from a connected project updates that
  // row, a key from another project opens a new one.
  {
    key: 'revenuecat',
    id: 'revenuecat',
    nameKey: 'integrations.providers.revenueCat',
    category: 'analytics_and_tracking',
    connect: 'credential',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    labelFields: ['name', 'revenuecat_project_id'],
    authLabelKey: 'integrations.authLabels.secretKey',
    allowsMultiple: true,
    logo: 'revenuecat',
  },
  {
    key: 'hardal_account',
    id: 'hardal_account',
    nameKey: 'integrations.providers.hardal',
    category: 'analytics_and_tracking',
    connect: 'credential',
    configComponent: () => import('../../components/connectors/CredentialProviderConnector.vue'),
    labelFields: ['signal_id'],
    authLabelKey: 'integrations.authLabels.credentials',
    logo: 'hardal',
  },
]
